import { ref } from 'vue'
import { useUserGeo } from '~/composables/useUserGeo'
import { toGeoCoords, type GeoCoords } from '~/utils/typesenseGeo'

/**
 * Client-only geo coordinates for Typesense proximity ranking (KFT-4116).
 *
 * Resolves the viewer's location via `useUserGeo()` (`/api/geo`, KFT-4027) and
 * exposes it as a reactive `coords` ref, shared across every ranking surface
 * (listing, localities, homepage/promo/review showcases, federated autocomplete)
 * that used to rely on Algolia's `aroundLatLngViaIP`.
 *
 * Every consumer must tolerate `null`: `coords` stays null on SSR (KFT-3753 —
 * geo never resolves during SSR) and whenever geo is unavailable or the request
 * fails, so each surface renders without geo and simply reorders once
 * coordinates arrive on the client.
 */
export const useTypesenseGeoSort = () => {
  const coords = ref<GeoCoords | null>(null)

  const resolveGeoCoords = async (): Promise<GeoCoords | null> => {
    // KFT-3753 guard mirrors useUserGeo: never resolve geo during SSR.
    if (!import.meta.client) return null
    const geo = await useUserGeo().getUserGeo()
    coords.value = toGeoCoords(geo)
    return coords.value
  }

  return { coords, resolveGeoCoords }
}

/**
 * Calls `refetch` exactly once, once `geoCoordsPromise` (from
 * `resolveGeoCoords()`) settles, but only if geo actually resolved.
 *
 * KFT-4116 race: folding `coords` into a `watch()`-based refetch trigger
 * silently drops the refetch when the promise resolves before that watcher
 * is registered — `watch()` captures its "old" value at registration time,
 * so if geo has already resolved by then, no "change" is ever observed and
 * the list is stuck showing geo-less results. Chaining directly off the
 * promise has no such registration-order dependency: it fires once,
 * whichever side wins the race.
 */
export const refetchOnceGeoResolved = (
  geoCoordsPromise: Promise<GeoCoords | null>,
  refetch: () => void
): void => {
  geoCoordsPromise.then((coords) => {
    if (coords) refetch()
  })
}
